import { useCallback, useEffect, useState } from 'react'
import {
  listOrcamentosMateriais,
  addOrcamentoMaterial,
  updateOrcamentoMaterial,
} from '@services/crud.js'
import {
  escolherOrcamentoELancar,
  excluirOrcamentoComLancamento,
  marcarMaterialComprado,
  upsertLancamentoMaterial,
} from '@services/gestao.js'
import type { OrcamentoMaterial } from '@services/db.js'
import { createId } from '@utils/createId.js'

function grupoIdForMaterial(material: string, map: Map<string, string>): string {
  const key = material.trim().toLowerCase()
  const existing = map.get(key)
  if (existing) return existing
  const id = createId()
  map.set(key, id)
  return id
}

export function useOrcamentosMateriais() {
  const [orcamentos, setOrcamentos] = useState<OrcamentoMaterial[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      setOrcamentos(await listOrcamentosMateriais())
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const add = useCallback(
    async (data: Omit<OrcamentoMaterial, 'id' | 'criadoEm' | 'atualizadoEm'>) => {
      await addOrcamentoMaterial(data)
      await load()
    },
    [load],
  )

  const addBulk = useCallback(
    async (items: Omit<OrcamentoMaterial, 'id' | 'criadoEm' | 'atualizadoEm'>[]) => {
      const current = await listOrcamentosMateriais()
      const grupoMap = new Map<string, string>()
      for (const o of current) {
        grupoMap.set(o.material.trim().toLowerCase(), o.grupoId)
      }
      for (const item of items) {
        const grupoId = grupoIdForMaterial(item.material, grupoMap)
        await addOrcamentoMaterial({ ...item, grupoId })
      }
      await load()
    },
    [load],
  )

  const update = useCallback(
    async (id: number, patch: Partial<Omit<OrcamentoMaterial, 'id' | 'criadoEm'>>) => {
      await updateOrcamentoMaterial(id, patch)
      if (patch.status === 'comprado') await upsertLancamentoMaterial(id, 'pago')
      else if (patch.status === 'escolhido') await upsertLancamentoMaterial(id, 'previsto')
      await load()
    },
    [load],
  )

  const remove = useCallback(
    async (id: number) => {
      await excluirOrcamentoComLancamento(id)
      await load()
    },
    [load],
  )

  const escolher = useCallback(
    async (grupoId: string, escolhidoId: number) => {
      await escolherOrcamentoELancar(grupoId, escolhidoId)
      await load()
    },
    [load],
  )

  const marcarPago = useCallback(
    async (id: number) => {
      await marcarMaterialComprado(id)
      await load()
    },
    [load],
  )

  const grupos = orcamentos.reduce<Record<string, OrcamentoMaterial[]>>((acc, o) => {
    ;(acc[o.grupoId] ??= []).push(o)
    return acc
  }, {})

  return {
    orcamentos,
    grupos,
    isLoading,
    reload: load,
    add,
    addBulk,
    update,
    remove,
    escolher,
    marcarPago,
  }
}
